import { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Badge,
  Modal,
  Button,
  Snackbar,
  Alert,
  Popover,
  Tooltip,
  CircularProgress
} from "@mui/material";
// import { AndroidMockup, IPadMockup } from "react-device-mockup";
import { useNavigate } from "react-router-dom";
import { HexColorPicker } from "react-colorful";
import { extractColors } from "extract-colors";
import { QRCodeCanvas } from "qrcode.react";
import domtoimage from "dom-to-image";

// Iconos
import EditIcon from "@mui/icons-material/Edit";
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import ColorLensIcon from '@mui/icons-material/ColorLens';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import RestoreIcon from '@mui/icons-material/Restore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import QrCodeIcon from "@mui/icons-material/QrCode";
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';

// Componentes externos
import { OrderProductCard, ProductCard } from "../../calls/menu_clients/cards";
import { WorldCupRenderer } from "../../calls/menu_clients/world_cup_templates/WorldCupRenderer";
import { worldCupThemeAssets } from "../../calls/menu_clients/world_cup_templates/themeAssets";
import CardsMenuStyles from "./cards_menu_styles";
import BackgroundImageSelectionDialog from "../image_components/background_image_selection_dialog";
import { SendButton } from "../../../../components/buttons";
import { makeRequest } from "../../../../assets/functions_helper";
import imagenLocal from "../../../../assets/images/sections.jpg";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// --- UTILIDADES MEJORADAS ---
const getLuminance = (hex) => {
  if (!hex) return 255;
  const c = hex.substring(1);
  const rgb = parseInt(c, 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = (rgb >> 0) & 0xff;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const getContrastColor = (hex) => {
  if (!hex) return '#ffffff';
  const luma = getLuminance(hex);
  return luma > 160 ? '#000000' : '#ffffff';
};

// Calcular saturación de un color (simplificado)
const getSaturation = (hex) => {
  if (!hex) return 0;
  const c = hex.substring(1);
  const rgb = parseInt(c, 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = (rgb >> 0) & 0xff;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max === 0) return 0;
  return (max - min) / max * 100;
};

// Filtrar colores no deseados (blancos, negros, grises)
const filterUnwantedColors = (colors) => {
  return colors.filter(color => {
    const luminance = getLuminance(color.hex);
    const saturation = getSaturation(color.hex);

    // Excluir colores muy claros (blancos) o muy oscuros (negros)
    // y colores con baja saturación (grises)
    return luminance > 20 && luminance < 230 && saturation > 15;
  });
};

// --- ESTILOS COMPARTIDOS ---
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "#ffffff",
  boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  p: 4,
  borderRadius: "24px",
  minWidth: "340px",
  outline: 'none',
  border: '1px solid rgba(0,0,0,0.05)'
};

const actionButtonStyle = {
  backgroundColor: '#C5A059',
  color: '#ffffff',
  fontWeight: 700,
  py: 1.5,
  borderRadius: '12px',
  textTransform: 'none',
  fontSize: '1rem',
  boxShadow: '0 4px 12px rgba(197, 160, 89, 0.3)',
  '&:hover': { backgroundColor: '#B38E42', boxShadow: '0 6px 16px rgba(197, 160, 89, 0.4)' }
};

const inputContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  mb: 2,
  p: 1.5,
  borderRadius: '12px',
  border: '1px solid #f0f0f0',
  backgroundColor: '#fff'
};

const previewBoxStyle = {
  display: 'flex',
  justifyContent: 'center',
  mb: 3,
  p: 3,
  bgcolor: '#f8f9fa',
  borderRadius: '16px',
  border: '1px dashed #e0e0e0'
};

// --- COMPONENTE: SELECTOR DE COLOR ---
const CustomColorPicker = ({ label, color, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => { setAnchorEl(event.currentTarget); };
  const handleClose = () => { setAnchorEl(null); };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  return (
    <Box sx={inputContainerStyle}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#999', fontFamily: 'monospace', textTransform: 'uppercase' }}>{color}</Typography>
        <Box onClick={handleClick} sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: color, border: '2px solid #fff', boxShadow: '0 0 0 1px #ddd', cursor: 'pointer', transition: 'transform 0.1s', '&:hover': { transform: 'scale(1.1)' } }} />
      </Box>
      <Popover id={id} open={open} anchorEl={anchorEl} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }} PaperProps={{ sx: { p: 2, borderRadius: '16px', boxShadow: '0 10px 40px rgba(0,0,0,0.1)' } }}>
        <HexColorPicker color={color} onChange={onChange} />
      </Popover>
    </Box>
  );
};

// --- COMPONENTE DRAG SCROLL ---
const DragScrollWrapper = ({ children, style }) => {
  const scrollRef = useRef(null);
  let isDown = false;
  let startY;
  let scrollTop;

  const handleMouseDown = (e) => {
    isDown = true;
    scrollRef.current.classList.add("active");
    startY = e.pageY - scrollRef.current.offsetTop;
    scrollTop = scrollRef.current.scrollTop;
  };
  const handleMouseLeave = () => { isDown = false; };
  const handleMouseUp = () => { isDown = false; };
  const handleMouseMove = (e) => {
    if (!isDown) return;
    e.preventDefault();
    const y = e.pageY - scrollRef.current.offsetTop;
    const walk = (y - startY) * 1;
    scrollRef.current.scrollTop = scrollTop - walk;
  };

  return (
    <Box
      ref={scrollRef}
      sx={{
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        cursor: "grab",
        WebkitOverflowScrolling: "touch",
        userSelect: "none",
        position: 'relative',
        '&::-webkit-scrollbar': { display: 'none' }, // Evita que se vea el scrollbar en mockup tapando el lado derecho
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        ...style
      }}
      onMouseDown={handleMouseDown}
      onMouseLeave={handleMouseLeave}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
    >
      {children}
    </Box>
  );
};

// ==========================================
// COMPONENTE: TEMAS MUNDIAL 2026
// ==========================================
const worldCupThemes = Object.values(worldCupThemeAssets);

const WorldCupThemeModal = ({ open, onClose, onSelectTheme }) => {
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{ ...modalStyle, maxWidth: '450px', textAlign: 'center' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: '50%', color: '#16a34a' }}>
            <SportsSoccerIcon sx={{ fontSize: 30 }} />
          </Box>
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Temas Mundial 2026</Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
          Selecciona tu país para aplicar un tema inmutable con colores y animaciones especiales.
        </Typography>
        <Grid container spacing={2}>
          {worldCupThemes.map((theme) => (
            <Grid item xs={6} key={theme.id}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => onSelectTheme(theme)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  py: 2,
                  borderColor: '#ddd',
                  borderRadius: '16px',
                  color: '#333',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: theme.primary,
                    backgroundColor: theme.primary + '10',
                  }
                }}
              >
                <Typography variant="h4">{theme.flag}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{theme.name}</Typography>
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Modal>
  );
};

// ==========================================
// COMPONENTE: MODAL DE CARGA MEJORADO
// ==========================================
const MagicThemeGenerator = ({ open, onClose, onColorsExtracted, isDarkMode, restaurantId }) => {
  const [imagePreview, setImagePreview] = useState(null);
  const [dominantColors, setDominantColors] = useState([]);

  const [imageSelectionOpen, setImageSelectionOpen] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);

  // Función mejorada para extraer colores
  const extractBestColors = async (src) => {
    setIsExtracting(true);
    try {
      const colors = await extractColors(src, {
        crossOrigin: "anonymous",
        distance: 0.1,
        saturationDistance: 0.2,
        lightnessDistance: 0.2
      });

      // Filtrar colores no deseados
      const filteredColors = filterUnwantedColors(colors);

      // Si después de filtrar tenemos colores, usar esos
      let bestColors = filteredColors;
      if (filteredColors.length === 0) {
        // Si no hay colores después de filtrar, tomar los originales pero ordenar por saturación
        bestColors = [...colors].sort((a, b) => {
          const satA = getSaturation(a.hex);
          const satB = getSaturation(b.hex);
          return satB - satA; // Mayor saturación primero
        });
      }

      // Tomar hasta 3 colores más vibrantes
      const topColors = bestColors
        .slice(0, 3)
        .map(c => c.hex);

      setDominantColors(topColors);

    } catch (error) {
      console.error("Error extrayendo colores:", error);
      setDominantColors([]);
    } finally {
      setIsExtracting(false);
    }
  };

  const handleSelectImage = (img) => {
    if (img && img.image_url) {
      const src = img.image_url;
      setImagePreview(src);
      setDominantColors([]);

      // Bypass CORS para poder extraer colores en un canvas sin que sea bloqueado por el navegador
      const proxySrc = src.startsWith('http')
        ? `https://images.weserv.nl/?url=${encodeURIComponent(src)}`
        : src;

      extractBestColors(proxySrc);
    }
  };

  const handleApply = () => {
    if (dominantColors.length > 0) {
      onColorsExtracted(dominantColors, isDarkMode);
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box sx={{ ...modalStyle, maxWidth: '400px', textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: '50%', color: '#16a34a' }}>
              <AutoFixHighIcon sx={{ fontSize: 30 }} />
            </Box>
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Extraer Colores para Elementos</Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
            Sube una imagen para extraer colores vibrantes que se aplicarán a botones, precios, bordes e iconos.
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Box onClick={() => !isExtracting && setImageSelectionOpen(true)} sx={{ border: '2px dashed #e0e0e0', borderRadius: '16px', p: 2, cursor: isExtracting ? 'not-allowed' : 'pointer', transition: '0.2s', '&:hover': isExtracting ? {} : { borderColor: '#16a34a', bgcolor: '#f0fdf4' }, height: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', backgroundColor: '#fafafa', opacity: isExtracting ? 0.7 : 1 }}>
              {isExtracting ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, zIndex: 2 }}>
                  <CircularProgress size={40} thickness={4} sx={{ color: '#16a34a' }} />
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#16a34a' }}>Extrayendo magia...</Typography>
                </Box>
              ) : imagePreview ? (
                <img src={imagePreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'contain', zIndex: 1 }} />
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#999' }}>Haz click para seleccionar una imagen de tu galería</Typography>
              )}
            </Box>
          </Box>

          {dominantColors.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#999', mb: 1, display: 'block', textTransform: 'uppercase' }}>Colores Vibrantes Detectados</Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 2 }}>
                Se aplicarán a: Botones • Precios • Bordes • Iconos
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
                {dominantColors.map((color, i) => (
                  <Box key={i} sx={{ textAlign: 'center' }}>
                    <Tooltip title={color}>
                      <Box sx={{
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        bgcolor: color,
                        border: '2px solid #fff',
                        boxShadow: '0 0 0 1px #ddd',
                        mb: 0.5,
                        mx: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography variant="caption" sx={{
                          color: getContrastColor(color),
                          fontWeight: 700,
                          fontSize: '0.6rem'
                        }}>
                          {i + 1}
                        </Typography>
                      </Box>
                    </Tooltip>
                    <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontFamily: 'monospace' }}>
                      {color}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Button
            fullWidth
            variant="contained"
            onClick={handleApply}
            disabled={dominantColors.length === 0 || isExtracting}
            startIcon={<CheckCircleIcon />}
            sx={{ ...actionButtonStyle, mt: 0, bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}
          >
            Aplicar a Elementos
          </Button>
        </Box>
      </Modal>

      {imageSelectionOpen && (
        <BackgroundImageSelectionDialog
          open={imageSelectionOpen}
          handleClose={() => setImageSelectionOpen(false)}
          restaurantId={restaurantId}
          onSelectImage={handleSelectImage}
        />
      )}
    </>
  );
};

// ==========================================
// MODALES DE ESTILO
// ==========================================

const CallWaiterStyleModal = ({ open, onClose, onSave, currentStyles }) => {
  const [styles, setStyles] = useState({ backgroundColor: "#C5A059", textColor: "#ffffff", iconColor: "#ffffff" });
  useEffect(() => {
    if (open) setStyles({
      backgroundColor: currentStyles?.backgroundColor || "#C5A059",
      textColor: currentStyles?.textColor || "#ffffff",
      iconColor: currentStyles?.iconColor || "#ffffff"
    });
  }, [open, currentStyles]);
  const handleSave = () => { onSave(styles); onClose(); };
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ color: "#000", fontWeight: 800 }}>Botón Llamar Mesero</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
        <Box sx={previewBoxStyle}>
          <Button
            variant="contained"
            startIcon={<NotificationsIcon />}
            sx={{
              backgroundColor: styles.backgroundColor,
              color: styles.textColor,
              fontWeight: 700,
              px: 4,
              py: 1.5,
              borderRadius: '2rem',
              textTransform: 'none',
              boxShadow: `0 4px 12px ${styles.backgroundColor}40`,
              '&:hover': {
                opacity: 0.9,
                backgroundColor: styles.backgroundColor
              }
            }}
          >
            Llamar mesero
          </Button>
        </Box>
        <CustomColorPicker label="Color de Botón" color={styles.backgroundColor} onChange={(c) => setStyles({ ...styles, backgroundColor: c })} />
        <CustomColorPicker label="Color del Texto" color={styles.textColor} onChange={(c) => setStyles({ ...styles, textColor: c })} />
        <CustomColorPicker label="Color del Icono" color={styles.iconColor} onChange={(c) => setStyles({ ...styles, iconColor: c })} />
        <Button fullWidth variant="contained" onClick={handleSave} sx={actionButtonStyle}>Guardar Cambios</Button>
      </Box>
    </Modal>
  );
};

const ViewCartStyleModal = ({ open, onClose, onSave, currentStyles }) => {
  const [styles, setStyles] = useState({ backgroundColor: "#C5A059", textColor: "#ffffff", iconColor: "#ffffff" });
  useEffect(() => { if (open) setStyles({ backgroundColor: currentStyles?.backgroundColor || "#C5A059", textColor: currentStyles?.textColor || "#ffffff", iconColor: currentStyles?.iconColor || "#ffffff" }); }, [open, currentStyles]);
  const handleSave = () => { onSave(styles); onClose(); };
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ color: "#000", fontWeight: 800 }}>Botón Ver Carrito</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
        <Box sx={previewBoxStyle}>
          <Button
            variant="contained"
            startIcon={<ShoppingBagIcon />}
            sx={{
              backgroundColor: styles.backgroundColor,
              color: styles.textColor,
              fontWeight: 700,
              px: 4,
              py: 1.5,
              borderRadius: '2rem',
              textTransform: 'none',
              boxShadow: `0 4px 12px ${styles.backgroundColor}40`,
              '&:hover': {
                opacity: 0.9,
                backgroundColor: styles.backgroundColor
              }
            }}
          >
            Ver Carrito (0)
          </Button>
        </Box>
        <CustomColorPicker label="Color de Botón" color={styles.backgroundColor} onChange={(c) => setStyles({ ...styles, backgroundColor: c })} />
        <CustomColorPicker label="Color del Texto" color={styles.textColor} onChange={(c) => setStyles({ ...styles, textColor: c })} />
        <CustomColorPicker label="Color del Icono" color={styles.iconColor} onChange={(c) => setStyles({ ...styles, iconColor: c })} />
        <Button fullWidth variant="contained" onClick={handleSave} sx={actionButtonStyle}>Guardar Cambios</Button>
      </Box>
    </Modal>
  );
};

const TableCodeStyleModal = ({ open, onClose, onSave, currentStyles, isDarkMode }) => {
  const [styles, setStyles] = useState({
    backgroundColor: isDarkMode ? "rgba(197, 160, 89, 0.1)" : "rgba(197, 160, 89, 0.08)",
    borderColor: "#C5A059",
    labelColor: isDarkMode ? "#AAAAAA" : "#666666",
    numberColor: "#C5A059"
  });
  const handleSave = () => { onSave(styles); onClose(); };
  const handleChange = (key, value) => { setStyles(prev => ({ ...prev, [key]: value })); };
  useEffect(() => {
    if (open) setStyles({
      backgroundColor: currentStyles?.backgroundColor || (isDarkMode ? "rgba(197, 160, 89, 0.1)" : "rgba(197, 160, 89, 0.08)"),
      borderColor: currentStyles?.borderColor || "#C5A059",
      labelColor: currentStyles?.labelColor || (isDarkMode ? "#AAAAAA" : "#666666"),
      numberColor: currentStyles?.numberColor || "#C5A059"
    });
  }, [open, currentStyles, isDarkMode]);
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{ ...modalStyle, maxHeight: '90vh', overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ color: "#000", fontWeight: 800 }}>Estilo Código de Mesa</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
        <Box sx={previewBoxStyle}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: styles.backgroundColor,
            px: 3,
            py: 1.5,
            borderRadius: '50px',
            border: `1px solid ${styles.borderColor}`,
            minWidth: '120px',
            justifyContent: 'center'
          }}>
            <Typography variant="caption" sx={{ color: styles.labelColor, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem' }}>Codigo Mesa</Typography>
            <Typography variant="h6" sx={{ color: styles.numberColor, fontWeight: 900, lineHeight: 1 }}>01</Typography>
          </Box>
        </Box>
        <CustomColorPicker label="Color de Fondo" color={styles.backgroundColor} onChange={(c) => handleChange('backgroundColor', c)} />
        <CustomColorPicker label="Color del Borde" color={styles.borderColor} onChange={(c) => handleChange('borderColor', c)} />
        <CustomColorPicker label="Texto 'Mesa'" color={styles.labelColor} onChange={(c) => handleChange('labelColor', c)} />
        <CustomColorPicker label="Número" color={styles.numberColor} onChange={(c) => handleChange('numberColor', c)} />
        <Button fullWidth variant="contained" onClick={handleSave} sx={actionButtonStyle}>Guardar Cambios</Button>
      </Box>
    </Modal>
  );
};

// --- COMPONENTE PARA MOSTRAR LOS COLORES (AHORA CLICKEABLE) ---
const ColorPaletteDisplay = ({ colors, onColorSelect }) => {
  if (!colors || colors.length === 0) return null;

  return (
    <Box sx={{
      position: 'absolute',
      top: 10,
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      gap: 1,
      backgroundColor: 'rgba(255,255,255,0.9)',
      padding: '6px 12px',
      borderRadius: '20px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      alignItems: 'center',
      zIndex: 1000,
      border: '1px solid rgba(0,0,0,0.05)'
    }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', whiteSpace: 'nowrap', fontSize: '0.7rem' }}>
        Colores aplicados:
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {colors.map((color, i) => (
          <Tooltip key={i} title={color}>
            <Box
              onClick={() => onColorSelect && onColorSelect(color)}
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: color,
                border: '1px solid white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                cursor: onColorSelect ? 'pointer' : 'default',
                transition: 'transform 0.1s',
                '&:hover': onColorSelect ? { transform: 'scale(1.2)' } : {}
              }}
            />
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};

// --- PREVIEW HEADER (MODIFICADO) ---
const PreviewHeader = ({
  menuSections,
  onEditBackground,
  headerBg,
  headerText,
  selectBg,
  selectText,
  logoUrl,
  onEditLogo,
  onEditTableCode,
  tableCodeStyles,
  isDarkMode,
  toggleDarkMode
}) => {
  return (
    <Box sx={{
      px: { xs: 2, sm: 3 }, // Ajustar px en móviles para que no se vea cortado (era 3 fijo)
      py: 2,
      backgroundColor: headerBg || (isDarkMode ? "#1E1E1E" : "#FFFFFF"),
      borderBottom: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box onClick={onEditLogo} sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 2 },
          position: 'relative'
        }}>
          <Box sx={{ position: 'relative' }}>
            <Box component="img"
              src={logoUrl || 'https://lh3.googleusercontent.com/aida-public/AB6AXuA6hR82T0AyZSauL044UIYESTK5raRF8lCbtjl6ErjHlqMAd8BiWBKyoeAPV9GlNRrvCwWBDfQOtEZuwaqAGFXYQYSOCTf_U-3YIurTaDdGxZl7gStyZ0iJXCPaJ462zDozm7Z4nJx3JIZ3GJeCr2COM9eH_XAPNt83_8R76ghqMC33jIUtNS5x-c9gYs198vQ1St_kDPGbkyrFzYwUwom5XMlZx8r8qnEeaZ7XKuNFvYwTJrtFIXI-eq2Hd3O9NAe9GsFHD4hcyyY'}
              alt="Logo"
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                objectFit: 'cover',
                border: isDarkMode ? '2px solid rgba(255,255,255,0.1)' : '2px solid rgba(0,0,0,0.05)'
              }}
            />
            <Box sx={{
              position: 'absolute',
              bottom: -4,
              right: -4,
              bgcolor: '#1c1c34',
              borderRadius: '50%',
              p: 0.5,
              display: 'flex',
              width: '20px',
              height: '20px',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid white'
            }}>
              <EditIcon sx={{ fontSize: 10, color: 'white' }} />
            </Box>
          </Box>
        </Box>

        <Box onClick={onEditTableCode} sx={{
          cursor: 'pointer',
          position: 'relative'
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1 },
            backgroundColor: tableCodeStyles.backgroundColor,
            padding: { xs: '6px 12px', sm: '6px 16px' },
            borderRadius: '50px',
            border: `1px solid ${tableCodeStyles.borderColor}`,
            transition: 'all 0.2s',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }
          }}>
            <Typography variant="caption" sx={{
              color: tableCodeStyles.labelColor,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: { xs: '0.5px', sm: '1px' },
              fontSize: { xs: '0.6rem', sm: '0.65rem' },
              whiteSpace: 'nowrap'
            }}>
              Codigo Mesa
            </Typography>
            <Typography variant="h6" sx={{
              color: tableCodeStyles.numberColor,
              fontWeight: 900,
              fontSize: '1.25rem'
            }}>
              01
            </Typography>
          </Box>
          <Box sx={{
            position: 'absolute',
            top: -8,
            right: -8,
            bgcolor: '#1c1c34',
            borderRadius: '50%',
            p: 0.5,
            display: 'flex',
            width: '20px',
            height: '20px',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid white'
          }}>
            <EditIcon sx={{ fontSize: 10, color: 'white' }} />
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          fullWidth
          startIcon={<RestaurantMenuIcon />}
          endIcon={<ExpandMoreIcon />}
          sx={{
            justifyContent: 'space-between',
            backgroundColor: selectBg || (isDarkMode ? '#2A2A2A' : '#F5F5F5'),
            color: selectText || (isDarkMode ? '#FFFFFF' : '#1A1A1A'),
            borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
            borderRadius: '16px',
            textTransform: 'none',
            fontWeight: 600,
            py: { xs: 1, sm: 1.5 },
            px: { xs: 2, sm: 3 },
            '&:hover': {
              borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
              backgroundColor: selectBg || (isDarkMode ? '#2A2A2A' : '#F5F5F5'),
              transform: 'translateY(-2px)'
            }
          }}
        >
          Todo el menú
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Cambiar tema claro/oscuro">
            <IconButton onClick={toggleDarkMode} sx={{
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
              color: isDarkMode ? '#FFFFFF' : '#1A1A1A',
              borderRadius: '12px',
              '&:hover': { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)' }
            }}>
              {isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Cambiar imagen de fondo">
            <IconButton onClick={onEditBackground} sx={{
              backgroundColor: '#1c1c34',
              color: 'white',
              borderRadius: '12px',
              '&:hover': { bgcolor: '#2b2b4e' }
            }}>
              <ColorLensIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

// --- PREVIEW SECTION ---
const PreviewSection = ({ section, onEditStyle, bodyTextColor, buttonColor: globalButtonColor, borderColor: globalBorderColor, isDarkMode, activeTheme }) => {
  const cardType = section.section_type || "default";

  // Obtener estilos de la sección (si existen) o usar valores por defecto globales/dark mode
  const productBg = isDarkMode ? "#1E1E1E" : "#FFFFFF";
  const productText = isDarkMode ? "#FFFFFF" : "#1A1A1A";
  const buttonColor = section.styles?.buttons_color || globalButtonColor;

  const sectionBackgroundImage = section.background_image_url || imagenLocal;

  return (
    <Box sx={{ mb: 6, px: { xs: 0, sm: 0.5, md: 1 } }}>
      <Box sx={{
        position: 'relative',
        height: '180px',
        borderRadius: '24px',
        overflow: 'hidden',
        mb: 4,
        boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
        '&:hover': { transform: 'scale(1.01)', transition: 'transform 0.3s' }
      }}>
        <Box sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${sectionBackgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: 1,
          filter: 'brightness(0.8)'
        }} />
        <Box sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 2
        }} />

        <Box sx={{
          position: 'relative',
          zIndex: 3,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          pl: 4,
          pr: 2
        }}>
          <Typography variant="h4" sx={{
            fontWeight: 900,
            color: '#FFFFFF',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            mb: 1
          }}>
            {section.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: '32px', height: '3px', bgcolor: buttonColor || '#C5A059', borderRadius: '2px' }} />
            <Typography variant="caption" sx={{
              color: 'rgba(255,255,255,0.9)',
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              {section.products.length} {section.products.length === 1 ? 'Plato' : 'Platos'}
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={activeTheme ? null : () => onEditStyle(section)}
          disabled={!!activeTheme}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 10,
            backgroundColor: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(10px)',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.3)',
            opacity: activeTheme ? 0.5 : 1,
            '&:hover': {
              backgroundColor: activeTheme ? 'transparent' : (buttonColor || '#C5A059'),
              borderColor: activeTheme ? 'rgba(255,255,255,0.3)' : (buttonColor || '#C5A059')
            }
          }}
        >
          <EditIcon />
        </IconButton>
      </Box>

      <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }} sx={{ px: 0 }}>
        {section.products.map((product) => (
          <Grid item xs={12} key={product.id}>
            <Box sx={{
              "& .MuiCard-root": {
                backgroundColor: `${productBg} !important`,
                boxShadow: `0 8px 20px rgba(0,0,0,0.12), 0 4px 12px ${buttonColor}80 !important`,
                color: `${productText} !important`,
                borderRadius: '20px !important',
                animation: activeTheme ? 'pulseBorder 2s infinite' : 'none',
                '@keyframes pulseBorder': {
                  '0%': { boxShadow: `0 0 0 0px ${buttonColor}40` },
                  '70%': { boxShadow: `0 0 0 10px rgba(0,0,0,0)` },
                  '100%': { boxShadow: `0 0 0 0px rgba(0,0,0,0)` }
                },
                '&:hover': {
                  transform: 'translateY(-4px) !important',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.15) !important'
                }
              },
              "& .MuiTypography-root": { color: `${productText} !important` },
              "& .MuiTypography-caption": {
                color: `${isDarkMode ? '#AAAAAA' : '#666666'} !important`,
                opacity: 0.8
              },
              "& .price-text": {
                color: `${buttonColor} !important`,
                fontWeight: 900,
                fontSize: '1.2rem'
              },
              "& .MuiIconButton-root": {
                backgroundColor: `${buttonColor} !important`,
                color: `${getContrastColor(buttonColor)} !important`,
                borderRadius: '12px !important',
                width: '40px !important',
                height: '40px !important',
                '&:hover': {
                  backgroundColor: `${buttonColor} !important`,
                  opacity: 0.9
                }
              }
            }}>
              {cardType === "default" ? (
                <ProductCard
                  product={product}
                  onSelect={() => { }}
                  buttonColorCards={buttonColor}
                  backgroundColorCards={productBg}
                  textColorCards={productText}
                />
              ) : (
                <OrderProductCard
                  product={product}
                  onSelect={() => { }}
                  buttonColorCards={buttonColor}
                  backgroundColorCards={productBg}
                  textColorCards={productText}
                />
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// --- FOOTER DENTRO DEL CONTENIDO ---
const PreviewFooter = ({ callWaiterStyles, viewCartStyles, isDarkMode, onEditCallWaiter, onEditViewCart }) => {
  return (
    <Box sx={{
      position: 'sticky',
      bottom: 10,
      left: 0,
      right: 0,
      zIndex: 1100,
      mx: { xs: 1, sm: 2, md: 3 },
      mb: 1,
      borderRadius: '20px',
      boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
      backgroundColor: isDarkMode ? 'rgba(30, 30, 30, 0.9)' : 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(20px)',
      border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
      py: 1,
      px: { xs: 2, sm: 3 }
    }}>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={6}>
          <Button
            variant="contained"
            onClick={onEditCallWaiter}
            startIcon={<NotificationsIcon />}
            sx={{
              backgroundColor: callWaiterStyles.backgroundColor,
              color: callWaiterStyles.textColor,
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '16px',
              px: 2,
              py: 1,
              width: '100%',
              boxShadow: `0 4px 12px ${callWaiterStyles.backgroundColor}40`,
              '&:hover': {
                opacity: 0.9,
                backgroundColor: callWaiterStyles.backgroundColor,
                transform: 'translateY(-2px)',
                boxShadow: `0 6px 16px ${callWaiterStyles.backgroundColor}60`
              }
            }}
          >
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="caption" sx={{
                display: 'block',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.55rem',
                opacity: 0.8
              }}>
                Llamar
              </Typography>
              <Typography variant="body2" sx={{
                fontWeight: 800,
                textTransform: 'uppercase',
                fontSize: '0.8rem'
              }}>
                Mesero
              </Typography>
            </Box>
          </Button>
        </Grid>

        <Grid item xs={6}>
          <Button
            variant="contained"
            onClick={onEditViewCart}
            startIcon={<ShoppingBagIcon />}
            sx={{
              backgroundColor: viewCartStyles.backgroundColor,
              color: viewCartStyles.textColor,
              borderRadius: '16px',
              px: 2,
              py: 1,
              width: '100%',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': {
                opacity: 0.9,
                transform: 'translateY(-2px)',
                boxShadow: `0 6px 16px ${viewCartStyles.backgroundColor}60`
              }
            }}
          >
            <Box sx={{ textAlign: 'left', position: 'relative' }}>
              <Typography variant="caption" sx={{
                display: 'block',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.55rem',
                opacity: 0.8
              }}>
                Ver Carrito
              </Typography>
              <Typography variant="body2" sx={{
                fontWeight: 800,
                textTransform: 'uppercase',
                fontSize: '0.8rem'
              }}>
                $ 84.000
              </Typography>
            </Box>
            <Badge
              badgeContent={3}
              color="primary"
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: 'white',
                  color: viewCartStyles.backgroundColor,
                  fontWeight: 900,
                  fontSize: '0.55rem',
                  minWidth: '16px',
                  height: '16px',
                  top: 0,
                  right: -10
                }
              }}
            />
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

// --- COMPONENTE PRINCIPAL ---
export default function PreviewMenu({ restaurantId, menuData, mobilePreview = false, setMenuInfoComplete, menuInfoComplete, onDirtyChange }) {
  const [styleModal, setStyleModal] = useState({ open: false, currentSection: null });
  const [logoUrl, setLogoUrl] = useState(menuInfoComplete.logo_url || "");
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(menuInfoComplete.background_image_url || "");
  const [imageUploadType, setImageUploadType] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const bgColor = menuInfoComplete.background_color;
    if (!bgColor) return false;
    const luma = getLuminance(bgColor);
    return luma < 128;
  });

  // ── Dirty state: se activa cuando el usuario modifica algo sin guardar ──
  const [isDirty, setIsDirty] = useState(false);

  // Helper para marcar como modificado y notificar al padre
  const markDirty = () => {
    setIsDirty(true);
    if (onDirtyChange) onDirtyChange(true);
  };

  const clearDirty = () => {
    setIsDirty(false);
    if (onDirtyChange) onDirtyChange(false);
  };

  // ── FIX: Estado para Tema del Mundial — lee theme_id guardado ──
  const [activeTheme, setActiveTheme] = useState(
    menuInfoComplete.theme_id && worldCupThemeAssets[menuInfoComplete.theme_id]
      ? menuInfoComplete.theme_id
      : null
  );
  const [worldCupModalOpen, setWorldCupModalOpen] = useState(false);

  // Estados para QR
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [tables, setTables] = useState([]);
  const [loadingTables, setLoadingTables] = useState(false);
  const qrRef = useRef(null);

  // Referencia a la configuración original para restaurar
  const initialMenuInfoRef = useRef(menuInfoComplete);

  // ESTADO PARA LOS COLORES PREDOMINANTES
  const [dominantColors, setDominantColors] = useState([]);

  // ESTADOS DE ESTILOS
  const [callWaiterStyles, setCallWaiterStyles] = useState({
    backgroundColor: menuInfoComplete.product_background_color || "#4CAF50",
    textColor: menuInfoComplete.footer_config?.call_waiter_text_color || "#ffffff",
    iconColor: menuInfoComplete.footer_config?.call_waiter_text_color || "#ffffff"
  });

  const [viewCartStyles, setViewCartStyles] = useState({
    backgroundColor: menuInfoComplete.footer_config?.view_cart_button_background || "#C5A059",
    textColor: menuInfoComplete.footer_config?.view_cart_button_text_color || "#ffffff",
    iconColor: menuInfoComplete.footer_config?.view_cart_button_text_color || "#ffffff"
  });

  const [tableCodeStyles, setTableCodeStyles] = useState({
    backgroundColor: menuInfoComplete.table_code_config?.background_color || "rgba(197, 160, 89, 0.08)",
    borderColor: menuInfoComplete.table_code_config?.border_color || "#C5A059",
    labelColor: menuInfoComplete.table_code_config?.label_color || "#666666",
    numberColor: menuInfoComplete.table_code_config?.number_color || "#C5A059"
  });

  // Colores para elementos (botones, precios, bordes, iconos)
  const [elementColors, setElementColors] = useState({
    buttonPrimary: menuInfoComplete.buttons_color || "#C5A059",
    priceColor: menuInfoComplete.buttons_color || "#C5A059",
    borderPrimary: menuInfoComplete.buttons_color || "#C5A059",
    iconPrimary: menuInfoComplete.buttons_color || "#C5A059",
    decorative: menuInfoComplete.buttons_color || "#C5A059"
  });

  const [sections, setSections] = useState([]);

  // Efecto para construir secciones respetando estilos existentes
  useEffect(() => {
    if (menuData) {
      setSections(prevSections =>
        menuData.map(section => {
          const existingSection = prevSections.find(s => s.id === section.id);
          const existingStyles = existingSection?.styles || {};
          return {
            ...section,
            styles: {
              buttons_color: existingStyles.buttons_color || section.button_color_cards || elementColors.buttonPrimary,
              product_border_color: existingStyles.product_border_color || section.border_color_cards || elementColors.borderPrimary,
            }
          };
        })
      );
    }
  }, [menuData, elementColors]);

  // ── FIX: Aplicar colores del tema guardado al montar (solo una vez) ──
  useEffect(() => {
    const savedThemeId = menuInfoComplete.theme_id;
    if (savedThemeId && worldCupThemeAssets[savedThemeId]) {
      const theme = worldCupThemeAssets[savedThemeId];
      const buttonTextColor = theme.isDarkMode ? getContrastColor(theme.primary) : '#ffffff';
      setElementColors({
        buttonPrimary: theme.primary,
        priceColor: theme.primary,
        borderPrimary: theme.primary,
        iconPrimary: theme.primary,
        decorative: theme.secondary
      });
      setCallWaiterStyles({ backgroundColor: theme.primary, textColor: buttonTextColor, iconColor: buttonTextColor });
      setViewCartStyles({ backgroundColor: theme.primary, textColor: buttonTextColor, iconColor: buttonTextColor });
      setTableCodeStyles(prev => ({ ...prev, borderColor: theme.primary, numberColor: theme.primary }));
      setIsDarkMode(theme.isDarkMode);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo al montar

  // Aplicar modo oscuro a los temas
  useEffect(() => {
    if (isDarkMode) {
      setTableCodeStyles(prev => ({
        ...prev,
        backgroundColor: "rgba(197, 160, 89, 0.1)",
        labelColor: "#AAAAAA"
      }));
    } else {
      setTableCodeStyles(prev => ({
        ...prev,
        backgroundColor: "rgba(197, 160, 89, 0.08)",
        labelColor: "#666666"
      }));
    }
  }, [isDarkMode]);

  // MODALES
  const [callWaiterModalOpen, setCallWaiterModalOpen] = useState(false);
  const [viewCartModalOpen, setViewCartModalOpen] = useState(false);
  const [tableCodeModalOpen, setTableCodeModalOpen] = useState(false);
  const [magicModalOpen, setMagicModalOpen] = useState(false);
  const [dialogEditBackgroundImage, setDialogEditBackgroundImage] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  const navigate = useNavigate();

  // --- LÓGICA MEJORADA PARA APLICAR COLORES ---
  const applyExtractedColorsToElements = (colors) => {
    if (!colors || colors.length === 0) return;

    const [color1, color2] = colors;

    const primaryColor = color1;
    const accentColor = color2 || color1;

    setElementColors({
      buttonPrimary: primaryColor,
      priceColor: primaryColor,
      borderPrimary: primaryColor,
      iconPrimary: primaryColor,
      decorative: accentColor
    });

    const buttonTextColor = getContrastColor(primaryColor);

    setCallWaiterStyles({
      backgroundColor: primaryColor,
      textColor: buttonTextColor,
      iconColor: buttonTextColor
    });

    setViewCartStyles({
      backgroundColor: primaryColor,
      textColor: buttonTextColor,
      iconColor: buttonTextColor
    });

    setTableCodeStyles(prev => ({
      ...prev,
      borderColor: primaryColor,
      numberColor: primaryColor
    }));

    const newSections = sections.map(section => ({
      ...section,
      styles: {
        ...section.styles,
        buttons_color: primaryColor,
        product_border_color: primaryColor
      }
    }));

    setSections(newSections);
    setDominantColors(colors);
    markDirty();
  };

  // --- NUEVA FUNCIÓN PARA SELECCIONAR UN COLOR DE LA PALETA ---
  const handleSelectDominantColor = (colorHex) => {
    if (!colorHex) return;
    const primaryColor = colorHex;
    // Usar el segundo color de la paleta como acento si existe, si no, el mismo primario
    const accentColor = dominantColors.length > 1 ? dominantColors[1] : primaryColor;

    setElementColors({
      buttonPrimary: primaryColor,
      priceColor: primaryColor,
      borderPrimary: primaryColor,
      iconPrimary: primaryColor,
      decorative: accentColor
    });

    const buttonTextColor = getContrastColor(primaryColor);

    setCallWaiterStyles({
      backgroundColor: primaryColor,
      textColor: buttonTextColor,
      iconColor: buttonTextColor
    });

    setViewCartStyles({
      backgroundColor: primaryColor,
      textColor: buttonTextColor,
      iconColor: buttonTextColor
    });

    setTableCodeStyles(prev => ({
      ...prev,
      borderColor: primaryColor,
      numberColor: primaryColor
    }));

    const newSections = sections.map(section => ({
      ...section,
      styles: {
        ...section.styles,
        buttons_color: primaryColor,
        product_border_color: primaryColor
      }
    }));

    setSections(newSections);
    setSnackbarMessage(`🎨 Color ${colorHex} aplicado como primario`);
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    markDirty();
  };

  const handleMagicApply = (colors) => {
    applyExtractedColorsToElements(colors);
    setMagicModalOpen(false);
    setSnackbarMessage("🎨 ¡Colores vibrantes aplicados a botones, precios, bordes e iconos!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    // markDirty ya se llama dentro de applyExtractedColorsToElements
  };

  const handleApplyWorldCupTheme = (theme) => {
    setActiveTheme(theme.id);
    setIsDarkMode(theme.isDarkMode);
    markDirty();
    setElementColors({
      buttonPrimary: theme.primary,
      priceColor: theme.primary,
      borderPrimary: theme.primary,
      iconPrimary: theme.primary,
      decorative: theme.secondary
    });

    const buttonTextColor = theme.isDarkMode ? getContrastColor(theme.primary) : '#ffffff';

    setCallWaiterStyles({
      backgroundColor: theme.primary,
      textColor: buttonTextColor,
      iconColor: buttonTextColor
    });
    setViewCartStyles({
      backgroundColor: theme.primary,
      textColor: buttonTextColor,
      iconColor: buttonTextColor
    });
    setTableCodeStyles(prev => ({
      ...prev,
      borderColor: theme.primary,
      numberColor: theme.primary
    }));

    setSections(prev => prev.map(section => ({
      ...section,
      styles: {
        ...section.styles,
        buttons_color: theme.primary,
        product_border_color: theme.primary
      }
    })));

    setWorldCupModalOpen(false);
    setSnackbarMessage(`⚽ ¡Tema del Mundial de ${theme.name} aplicado! (Inmutable)`);
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleOpenStyleModal = (section) => setStyleModal({ open: true, currentSection: section });
  const handleCloseStyleModal = () => setStyleModal({ open: false, currentSection: null });
  const handleEditLogo = () => { setImageUploadType('logo'); setDialogEditBackgroundImage(true); };
  const handleEditBackgroundImage = () => { setImageUploadType('background'); setDialogEditBackgroundImage(true); };
  const toggleDarkMode = () => { setIsDarkMode(prev => !prev); markDirty(); };

  const handleSaveStyle = (sectionId, cardType, stylesData) => {
    const updatedSections = sections.map((section) => {
      if (section.order === sectionId) {
        return {
          ...section,
          section_type: cardType,
          background_image_url: stylesData.background_image_url || stylesData.styles?.background_image_url || section.background_image_url,
          styles: { ...section.styles, ...stylesData.styles }
        };
      }
      return section;
    });
    setSections(updatedSections);
    markDirty();
  };

  // Función para restaurar la configuración original
  const handleRestore = () => {
    const original = initialMenuInfoRef.current;
    setLogoUrl(original.logo_url || "");
    setBackgroundImageUrl(original.background_image_url || "");
    setIsDarkMode(() => {
      const bgColor = original.background_color;
      if (!bgColor) return false;
      return getLuminance(bgColor) < 128;
    });
    setCallWaiterStyles({
      backgroundColor: original.product_background_color || "#4CAF50",
      textColor: original.footer_config?.call_waiter_text_color || "#ffffff",
      iconColor: original.footer_config?.call_waiter_text_color || "#ffffff"
    });
    setViewCartStyles({
      backgroundColor: original.footer_config?.view_cart_button_background || "#C5A059",
      textColor: original.footer_config?.view_cart_button_text_color || "#ffffff",
      iconColor: original.footer_config?.view_cart_button_text_color || "#ffffff"
    });
    setTableCodeStyles({
      backgroundColor: original.table_code_config?.background_color || "rgba(197, 160, 89, 0.08)",
      borderColor: original.table_code_config?.border_color || "#C5A059",
      labelColor: original.table_code_config?.label_color || "#666666",
      numberColor: original.table_code_config?.number_color || "#C5A059"
    });
    setElementColors({
      buttonPrimary: original.buttons_color || "#C5A059",
      priceColor: original.buttons_color || "#C5A059",
      borderPrimary: original.buttons_color || "#C5A059",
      iconPrimary: original.buttons_color || "#C5A059",
      decorative: original.buttons_color || "#C5A059"
    });
    if (original.sections) {
      setSections(original.sections.map(section => ({
        ...section,
        styles: {
          buttons_color: section.button_color_cards,
          product_border_color: section.border_color_cards,
        }
      })));
    }
    setDominantColors([]);
    setActiveTheme(null);
    setSnackbarMessage("🔄 Cambios restaurados a la versión guardada");
    setSnackbarSeverity("info");
    setSnackbarOpen(true);
  };

  // Función para valores predeterminados
  const handleDefault = () => {
    const defaultColor = "#C5A059";
    setIsDarkMode(false);
    setLogoUrl("");
    setBackgroundImageUrl("");
    setCallWaiterStyles({
      backgroundColor: "#4CAF50",
      textColor: "#ffffff",
      iconColor: "#ffffff"
    });
    setViewCartStyles({
      backgroundColor: defaultColor,
      textColor: "#ffffff",
      iconColor: "#ffffff"
    });
    setTableCodeStyles({
      backgroundColor: "rgba(197, 160, 89, 0.08)",
      borderColor: defaultColor,
      labelColor: "#666666",
      numberColor: defaultColor
    });
    setElementColors({
      buttonPrimary: defaultColor,
      priceColor: defaultColor,
      borderPrimary: defaultColor,
      iconPrimary: defaultColor,
      decorative: defaultColor
    });
    setSections(prev => prev.map(section => ({
      ...section,
      styles: {
        buttons_color: defaultColor,
        product_border_color: defaultColor,
      }
    })));
    setDominantColors([]);
    setActiveTheme(null);
    setSnackbarMessage("✨ Valores predeterminados aplicados");
    setSnackbarSeverity("info");
    setSnackbarOpen(true);
  };

  const handleSaveMenu = () => {
    const menuPayload = { ...menuInfoComplete, menu_id: menuInfoComplete.id || menuInfoComplete.menu_id };
    menuPayload.logo_url = logoUrl || "";
    menuPayload.background_image_url = backgroundImageUrl || "0";

    menuPayload.buttons_color = elementColors.buttonPrimary;
    menuPayload.product_background_color = callWaiterStyles.backgroundColor;

    menuPayload.background_color = isDarkMode ? "#121212" : "#FDFCFB";
    menuPayload.text_color = isDarkMode ? "#FFFFFF" : "#1A1A1A";

    menuPayload.header_config = {
      background_color: isDarkMode ? "#1E1E1E" : "#FFFFFF",
    };

    menuPayload.filter_config = {
      background_color: isDarkMode ? "#2A2A2A" : "#F5F5F5",
      text_color: isDarkMode ? "#FFFFFF" : "#1A1A1A"
    };

    menuPayload.footer_config = {
      background_color: isDarkMode ? "#1E1E1E" : "#FFFFFF",
      call_waiter_text_color: callWaiterStyles.textColor,
      view_cart_button_background: viewCartStyles.backgroundColor,
      view_cart_button_text_color: viewCartStyles.textColor
    };

    menuPayload.table_code_config = {
      background_color: tableCodeStyles.backgroundColor,
      border_color: tableCodeStyles.borderColor,
      label_color: tableCodeStyles.labelColor,
      number_color: tableCodeStyles.numberColor
    };

    menuPayload.sections = sections.map((section) => {
      const currentStyles = section.styles || {};
      return {
        ...section,
        background_color_cards: isDarkMode ? "#1E1E1E" : "#FFFFFF",
        text_color_cards: isDarkMode ? "#FFFFFF" : "#1A1A1A",
        button_color_cards: currentStyles.buttons_color,
        border_color_cards: currentStyles.product_border_color,
        styles: currentStyles,
        background_image_url: section.background_image_url
      };
    });

    menuPayload.element_colors = elementColors;
    menuPayload.dark_mode = isDarkMode;
    menuPayload.theme_id = activeTheme || "";

    makeRequest("POST", "menu_manager/edit_menu", menuPayload, "application/json", localStorage.getItem("token"), localStorage.getItem("tokenType"), () => navigate("/login"))
      .then(() => {
        clearDirty(); // Resetear dirty state tras guardar exitosamente
        if (setMenuInfoComplete) setMenuInfoComplete(prev => ({ ...prev, theme_id: activeTheme || "" }));
        setSnackbarMessage("🎯 Cambios guardados exitosamente");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      })
      .catch((error) => {
        console.error(error);
        setSnackbarMessage("❌ Error al guardar cambios");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      });
  };

  // --- NUEVAS FUNCIONES PARA QR ---
  const handleOpenQrDialog = async () => {
    setQrDialogOpen(true);
    setLoadingTables(true);
    try {
      const responseRestaurantId = await makeRequest(
        "GET",
        `restaurants/restaurant/info?restaurant_id=${restaurantId}`,
        {},
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType"),
        () => navigate("/login")
      );
      setTables(responseRestaurantId);
    } catch (error) {
      console.error("Error al cargar mesas:", error);
      setSnackbarMessage("Error al cargar las mesas");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoadingTables(false);
    }
  };

  const handleDownloadQR = () => {
    if (qrRef.current) {
      domtoimage
        .toPng(qrRef.current)
        .then((dataUrl) => {
          const link = document.createElement('a');
          link.download = `qr-mesa-${tables.name}.png`;
          link.href = dataUrl;
          link.click();
        })
        .catch((error) => {
          console.error('Error al descargar el QR:', error);
        });
    }
  };

  // RENDER CONTENT MEJORADO
  const renderContent = () => (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      backgroundImage: (!activeTheme && backgroundImageUrl) ? `url(${backgroundImageUrl})` : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundColor: (!activeTheme && !backgroundImageUrl) ? (isDarkMode ? "#121212" : "#FDFCFB") : 'transparent'
    }}>
      {!mobilePreview && dominantColors.length > 0 && !activeTheme && <ColorPaletteDisplay colors={dominantColors} onColorSelect={handleSelectDominantColor} />}

      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <DragScrollWrapper style={{ height: '100%', position: 'relative' }} id="preview-scroll-container">
          {activeTheme ? (
            <WorldCupRenderer
              sections={sections}
              theme={worldCupThemeAssets[activeTheme] || worldCupThemeAssets['colombia']}
              logoUrl={logoUrl}
              restaurantName="Menú"
              onProductSelect={() => { }}
            />
          ) : (
            <>
              <PreviewHeader
                menuSections={sections}
                onEditBackground={() => {
                  if (activeTheme) {
                    setSnackbarMessage("🔒 El fondo es inmutable por el Tema del Mundial.");
                    setSnackbarSeverity("warning");
                    setSnackbarOpen(true);
                  } else {
                    handleEditBackgroundImage();
                  }
                }}
                headerBg={isDarkMode ? "#1E1E1E" : "#FFFFFF"}
                headerText={isDarkMode ? "#FFFFFF" : "#1A1A1A"}
                selectBg={isDarkMode ? "#2A2A2A" : "#F5F5F5"}
                selectText={isDarkMode ? "#FFFFFF" : "#1A1A1A"}
                logoUrl={logoUrl}
                onEditLogo={() => {
                  if (activeTheme) {
                    setSnackbarMessage("🔒 El logo es inmutable en este tema.");
                    setSnackbarSeverity("warning");
                    setSnackbarOpen(true);
                  } else {
                    handleEditLogo();
                  }
                }}
                onEditTableCode={() => {
                  if (activeTheme) {
                    setSnackbarMessage("🔒 Este estilo es inmutable por el Tema del Mundial.");
                    setSnackbarSeverity("warning");
                    setSnackbarOpen(true);
                  } else {
                    setTableCodeModalOpen(true);
                  }
                }}
                tableCodeStyles={tableCodeStyles}
                isDarkMode={isDarkMode}
                toggleDarkMode={toggleDarkMode}
              />

              <Box sx={{ mt: 3, pb: '90px', px: { xs: 1, sm: 2, md: 3 } }}>
                {sections.map((section, index) => (
                  <PreviewSection
                    key={index}
                    section={section}
                    onEditStyle={handleOpenStyleModal}
                    bodyTextColor={isDarkMode ? "#FFFFFF" : "#1A1A1A"}
                    buttonColor={elementColors.buttonPrimary}
                    borderColor={elementColors.borderPrimary}
                    isDarkMode={isDarkMode}
                    activeTheme={activeTheme}
                  />
                ))}
              </Box>

              <PreviewFooter
                callWaiterStyles={callWaiterStyles}
                viewCartStyles={viewCartStyles}
                isDarkMode={isDarkMode}
                onEditCallWaiter={() => {
                  if (activeTheme) {
                    setSnackbarMessage("🔒 Este estilo es inmutable por el Tema del Mundial. Usa 'Restaurar' para cambiarlo.");
                    setSnackbarSeverity("warning");
                    setSnackbarOpen(true);
                  } else {
                    setCallWaiterModalOpen(true);
                  }
                }}
                onEditViewCart={() => {
                  if (activeTheme) {
                    setSnackbarMessage("🔒 Este estilo es inmutable por el Tema del Mundial. Usa 'Restaurar' para cambiarlo.");
                    setSnackbarSeverity("warning");
                    setSnackbarOpen(true);
                  } else {
                    setViewCartModalOpen(true);
                  }
                }}
              />
            </>
          )}
        </DragScrollWrapper>
      </Box>
    </Box>
  );

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', py: mobilePreview ? 4 : 8 }}>
      {mobilePreview && (
        <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', width: '100%', px: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              onClick={() => setMagicModalOpen(true)}
              variant="contained"
              disabled={!!activeTheme}
              startIcon={<AutoFixHighIcon />}
              sx={{
                backgroundColor: '#C5A059',
                opacity: activeTheme ? 0.5 : 1,
                color: 'white',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '50px',
                padding: '10px 24px',
                boxShadow: '0 4px 14px rgba(197, 160, 89, 0.4)',
                '&:hover': {
                  backgroundColor: '#B38E42',
                  transform: 'scale(1.05)'
                }
              }}
            >
              Extraer Colores
            </Button>

            <Button
              onClick={() => setWorldCupModalOpen(true)}
              variant="contained"
              startIcon={<SportsSoccerIcon />}
              sx={{
                backgroundColor: '#1c1c34',
                color: 'white',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '50px',
                padding: '10px 24px',
                boxShadow: '0 4px 14px rgba(28, 28, 52, 0.4)',
                '&:hover': {
                  backgroundColor: '#2b2b4e',
                  transform: 'scale(1.05)'
                }
              }}
            >
              Temas Mundial
            </Button>

            <Button
              onClick={toggleDarkMode}
              variant="outlined"
              startIcon={isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
              sx={{
                textTransform: 'none',
                borderRadius: '50px',
                padding: '10px 20px',
                borderColor: isDarkMode ? '#C5A059' : '#ddd',
                color: isDarkMode ? '#C5A059' : '#666',
                '&:hover': {
                  borderColor: '#C5A059',
                  backgroundColor: 'rgba(197, 160, 89, 0.05)'
                }
              }}
            >
              {isDarkMode ? 'Modo Claro' : 'Modo Oscuro'}
            </Button>

            <Button
              onClick={handleRestore}
              variant="outlined"
              startIcon={<RestoreIcon />}
              sx={{
                textTransform: 'none',
                borderRadius: '50px',
                padding: '10px 20px',
                borderColor: '#ddd',
                color: '#666',
                '&:hover': {
                  borderColor: '#C5A059',
                  backgroundColor: 'rgba(197, 160, 89, 0.05)'
                }
              }}
            >
              Restaurar
            </Button>

            <Button
              onClick={handleDefault}
              variant="outlined"
              startIcon={<AutoAwesomeIcon />}
              sx={{
                textTransform: 'none',
                borderRadius: '50px',
                padding: '10px 20px',
                borderColor: '#ddd',
                color: '#666',
                '&:hover': {
                  borderColor: '#C5A059',
                  backgroundColor: 'rgba(197, 160, 89, 0.05)'
                }
              }}
            >
              Predeterminado
            </Button>

            {/* NUEVO BOTÓN QR */}
            <Button
              onClick={handleOpenQrDialog}
              variant="outlined"
              startIcon={<QrCodeIcon />}
              sx={{
                textTransform: 'none',
                borderRadius: '50px',
                padding: '10px 20px',
                borderColor: '#ddd',
                color: '#666',
                '&:hover': {
                  borderColor: '#C5A059',
                  backgroundColor: 'rgba(197, 160, 89, 0.05)'
                }
              }}
            >
              QR Menu Sin Pedidos
            </Button>

            {/* Botón Guardar — solo visible si hay cambios sin guardar (dirty state) */}
            {isDirty && (
              <SendButton
                onClick={() => handleSaveMenu()}
                sx={{
                  maxWidth: '220px',
                  animation: 'pulseGreen 1.8s ease-in-out infinite',
                  '@keyframes pulseGreen': {
                    '0%': { boxShadow: '0 0 0 0 rgba(22, 163, 74, 0.4)' },
                    '70%': { boxShadow: '0 0 0 10px rgba(22, 163, 74, 0)' },
                    '100%': { boxShadow: '0 0 0 0 rgba(22, 163, 74, 0)' },
                  },
                }}
              >
                Guardar cambios
              </SendButton>
            )}
          </Box>

          {dominantColors.length > 0 && !activeTheme && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, p: 1.5, bgcolor: '#f8f9fa', borderRadius: '12px', width: '100%', maxWidth: '600px', justifyContent: 'center' }}>
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, whiteSpace: 'nowrap' }}>
                Colores aplicados:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {dominantColors.map((color, i) => (
                  <Tooltip key={i} title={color}>
                    <Box
                      onClick={() => handleSelectDominantColor(color)}
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: color,
                        border: '2px solid white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        cursor: 'pointer',
                        transition: 'transform 0.1s',
                        '&:hover': { transform: 'scale(1.2)' }
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
              <Typography variant="caption" sx={{ color: '#999', fontSize: '0.7rem' }}>
                (Botones • Precios • Bordes • Iconos)
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>{snackbarMessage}</Alert>
      </Snackbar>

      <Box sx={{ display: "flex", justifyContent: "center", position: 'relative', width: '100%' }}>
        <Box sx={{
          width: '390px',          // Phone width (iPhone 14 Pro size)
          maxWidth: '100%',
          mx: 'auto',
          bgcolor: activeTheme ? 'transparent' : (isDarkMode ? '#121212' : '#FDFCFB'),
          boxShadow: activeTheme
            ? '0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(252,209,22,0.3)'
            : '0 10px 40px rgba(0,0,0,0.1)',
          borderRadius: '36px',    // Phone-style rounded corners
          overflow: 'hidden',      // CRITICAL: clips the WorldCupRenderer's sticky bg
          minHeight: activeTheme ? '750px' : 'auto',
          position: 'relative',    // Stacking context for absolute children
        }}>
          {renderContent()}
        </Box>
      </Box>

      {styleModal.open && styleModal.currentSection && (
        <CardsMenuStyles
          open={styleModal.open}
          onClose={handleCloseStyleModal}
          onSave={handleSaveStyle}
          section={styleModal.currentSection}
          restaurantId={restaurantId}
        />
      )}

      <MagicThemeGenerator
        open={magicModalOpen}
        onClose={() => setMagicModalOpen(false)}
        onColorsExtracted={handleMagicApply}
        isDarkMode={isDarkMode}
        restaurantId={restaurantId}
      />

      <WorldCupThemeModal
        open={worldCupModalOpen}
        onClose={() => setWorldCupModalOpen(false)}
        onSelectTheme={handleApplyWorldCupTheme}
      />

      <CallWaiterStyleModal
        open={callWaiterModalOpen}
        onClose={() => setCallWaiterModalOpen(false)}
        onSave={(s) => { setCallWaiterStyles(s); markDirty(); }}
        currentStyles={callWaiterStyles}
      />

      <ViewCartStyleModal
        open={viewCartModalOpen}
        onClose={() => setViewCartModalOpen(false)}
        onSave={(s) => { setViewCartStyles(s); markDirty(); }}
        currentStyles={viewCartStyles}
      />

      <TableCodeStyleModal
        open={tableCodeModalOpen}
        onClose={() => setTableCodeModalOpen(false)}
        onSave={(s) => { setTableCodeStyles(s); markDirty(); }}
        currentStyles={tableCodeStyles}
        isDarkMode={isDarkMode}
      />

      <BackgroundImageSelectionDialog
        restaurantId={restaurantId}
        open={dialogEditBackgroundImage}
        handleClose={() => {
          setDialogEditBackgroundImage(false);
          setImageUploadType(null);
        }}
        onSelectImage={(imageData) => {
          if (imageUploadType === 'logo') {
            setLogoUrl(imageData.image_url);
          } else if (imageUploadType === 'background') {
            setBackgroundImageUrl(imageData.image_url);
          }
          markDirty();
          setDialogEditBackgroundImage(false);
          setImageUploadType(null);
        }}
      />

      {/* NUEVO DIÁLOGO PARA MOSTRAR QR */}
      <Modal open={qrDialogOpen} onClose={() => setQrDialogOpen(false)}>
        <Box sx={{
          ...modalStyle,
          width: '400px',
          maxWidth: '400px',
          textAlign: 'center'
        }}>
          {/* Encabezado del Modal */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: 'black' }}>
              Código QR para Menú Sin Pedidos
            </Typography>
            <IconButton onClick={() => setQrDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {loadingTables ? (
            <Box display="flex" justifyContent="center" p={3} minHeight="100px" />
          ) : (
            <Box display="flex" flexDirection="column" alignItems="center">

              {(() => {
                // Generamos la URL dinámicamente usando tables[0].number como en tu original
                const menuUrl = `${window.location.origin}/restaurantMenuDisplay/${tables.public_id}`;

                return (
                  <>
                    {/* Sección del Enlace Copiable */}
                    <Typography variant="body2" sx={{ color: 'black', width: '100%', textAlign: 'left', mb: 1, fontWeight: 600 }}>
                      Enlace directo al menú:
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        mb: 3,
                        p: 1,
                        pl: 1.5,
                        bgcolor: '#F5F5F5',
                        borderRadius: 2,
                        border: '1px solid #E0E0E0'
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          mr: 1,
                          color: '#333',
                          textAlign: 'left' // Aseguramos que el texto inicie a la izquierda
                        }}
                      >
                        {menuUrl}
                      </Typography>
                      <Tooltip title="Copiar enlace" placement="top">
                        <IconButton
                          size="small"
                          onClick={() => handleCopyLink(menuUrl)}
                          // El hover del botón de copiar ahora hace match con tu color #C5A059
                          sx={{ color: '#666', '&:hover': { color: '#C5A059', bgcolor: 'rgba(197,160,89,0.08)' } }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Sección del Código QR */}
                    <Typography variant="body2" sx={{ color: 'black', mb: 1, fontWeight: 600 }}>
                      O escanea este código QR:
                    </Typography>
                    <Box
                      ref={qrRef}
                      sx={{
                        p: 2,
                        bgcolor: 'white',
                        borderRadius: 3, // Bordes un poco más modernos
                        display: 'inline-block',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)', // Sombra más suave
                        border: '1px solid #EDEDED',
                        my: 1,
                      }}
                    >
                      <QRCodeCanvas
                        value={menuUrl}
                        size={200}
                        level="H"
                        includeMargin={false} // Retiramos el margen interno del canvas a favor del padding del Box
                      />
                    </Box>

                    {/* Botón de descarga actualizado */}
                    <Button
                      variant="contained"
                      onClick={handleDownloadQR}
                      disableElevation
                      sx={{
                        mt: 2,
                        width: '100%', // Expandido al 100% para mejor presencia en modales
                        borderRadius: 2,
                        py: 1,
                        fontWeight: 600,
                        textTransform: 'none',
                        bgcolor: '#C5A059',
                        '&:hover': { bgcolor: '#B38E42' },
                        color: 'white'
                      }}
                    >
                      Descargar QR
                    </Button>
                  </>
                );
              })()}
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
}